// Utility functions for handling common React/UI errors

/**
 * Suppress ResizeObserver errors which are harmless timing issues
 * that occur when DOM elements resize faster than ResizeObserver can process
 */
export const suppressResizeObserverErrors = () => {
  // Override console.error to filter ResizeObserver errors
  const originalConsoleError = console.error;
  console.error = (...args) => {
    const message = args[0]?.toString() || '';
    
    // Suppress ResizeObserver loop errors
    if (message.includes('ResizeObserver loop completed with undelivered notifications')) {
      return; // Don't log this error
    }
    
    // Suppress ResizeObserver loop limit exceeded errors  
    if (message.includes('ResizeObserver loop limit exceeded')) {
      return; // Don't log this error
    }
    
    // Log all other errors normally
    originalConsoleError.apply(console, args);
  };

  // Global error handler for uncaught ResizeObserver errors
  const handleGlobalError = (event) => {
    const error = event.error || event.reason;
    if (error && error.message && error.message.includes('ResizeObserver')) {
      event.preventDefault();
      return false;
    }
  };

  // Add listeners for both error types
  window.addEventListener('error', handleGlobalError, true);
  window.addEventListener('unhandledrejection', handleGlobalError, true);
};

/**
 * Create a debounced function to prevent rapid successive calls
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Safely handle component resize operations
 */
export const safeResizeHandler = (callback, delay = 100) => {
  return debounce((...args) => {
    try {
      callback(...args);
    } catch (error) {
      if (!error.message?.includes('ResizeObserver')) {
        console.error('Resize handler error:', error);
      }
    }
  }, delay);
};